// 024 y los 6 consentimientos nuevos (quirúrgico/anestesia/transfusión/
// telesalud-canal/telesalud-lopdp/voluntad-anticipada — Módulo H, Grupo 2
// ampliado, ver lib/consentimientos.ts): puerto a hce-chart de
// packages/hce/src/hce/consentimiento/Formulario024Consentimiento.tsx,
// generalizado para cualquier código de ese catálogo en vez de solo "024"
// (hallazgo de la auditoría de cumplimiento: "el sistema no distingue tipo
// de consentimiento"). Wizard de varios pasos (cargando→formulario→
// firma-paciente→firma-profesional→procesando→listo) — no pasa por
// MotorFormulario.tsx (Grupo 1) a propósito: ese componente guarda y extrae
// en un solo paso, y acá hace falta un paso intermedio de firma manuscrita
// del paciente entre medio. Reusa `FormularioQuestionnaire` (captura de
// respuestas) y `PanelFirmaImpresion` (PDF+firma profesional, vía su
// `onFirmado`) — los mismos mecanismos del Grupo 1, no una implementación
// aparte.
//
// Es a la vez la página de ruta (lee :id/?encounter= y aplica el gate de
// especialidad, mismo patrón que MotorFormularioPage.tsx) y el wizard. El
// código llega por prop (una ruta estática por código en routes.tsx, mismo
// patrón que FORMULARIOS_DISCRETOS) en vez de por param de URL — así
// `formulario/024` no cambia para los links ya existentes de
// SelectorFormularios.tsx.
import type { DocumentReference, Encounter, Patient, Questionnaire, QuestionnaireResponse, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams, useSearchParams } from 'react-router';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { codigosFormularioDeEspecialidad } from '@/lib/catalogoEspecialidades';
import { completarFechaHoraDecision, decisionDe, errores024, firmaPorRepresentante, paresConsentimiento024, servicioAtencion024, type MetodoFirma } from '@/lib/consentimiento024';
import { metaConsentimiento } from '@/lib/consentimientos';
import { useEspecialidadDelPracticante } from '@/lib/useEspecialidadDelPracticante';
import type { PatientChartOutletContext } from '@/paciente/PatientChartPage';
import { CapturaFirmaPaciente, type FirmaPacienteCapturada } from './CapturaFirmaPaciente';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from './EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from './useDatosEncabezadoMsp';
import { CapturaFirmaTestigo, type FirmaTestigoCapturada } from './CapturaFirmaTestigo';
import { construirConsentimientoInformado } from './extractors';
import { FormularioQuestionnaire } from './FormularioQuestionnaire';
import { PanelFirmaImpresion } from './PanelFirmaImpresion';
import { subirFirmaComoDocumentReference } from './subirFirmaDataUrl';

export interface MotorFormularioConsentimientoProps {
  readonly codigo: string;
}

type Estado =
  | { fase: 'cargando' }
  | { fase: 'no-encontrado' }
  | { fase: 'error'; mensaje: string }
  | { fase: 'formulario'; questionnaire: Questionnaire }
  | { fase: 'firma-paciente'; questionnaire: Questionnaire; response: QuestionnaireResponse; aceptado: boolean; porRepresentante: boolean }
  | { fase: 'firma-testigo'; questionnaire: Questionnaire; response: QuestionnaireResponse }
  | { fase: 'firma-profesional'; questionnaire: Questionnaire; response: QuestionnaireResponse; firmante: { rol: 'paciente' | 'testigo'; docRefId: string; metodo?: MetodoFirma; testigo?: { nombre: string; cedula: string } } }
  | { fase: 'procesando'; mensaje: string }
  | { fase: 'listo' };

const PASOS = ['pasoFormulario', 'pasoFirmaPaciente', 'pasoFirmaProfesional', 'pasoListo'] as const;

function pasoActivo(fase: Estado['fase']): number {
  if (fase === 'formulario') return 0;
  if (fase === 'firma-paciente' || fase === 'firma-testigo') return 1;
  if (fase === 'firma-profesional' || fase === 'procesando') return 2;
  if (fase === 'listo') return 3;
  return 0;
}

function Indicador({ fase }: { fase: Estado['fase'] }): JSX.Element {
  const { t } = useTranslation();
  const activo = pasoActivo(fase);
  return (
    <ol className="flex flex-wrap gap-4 text-xs font-mono uppercase tracking-wide">
      {PASOS.map((paso, i) => (
        <li key={paso} className={i === activo ? 'font-semibold text-hce-pine' : i < activo ? 'text-hce-ink' : 'text-hce-muted'}>
          {i + 1}. {t(`consentimiento.${paso}`)}
        </li>
      ))}
    </ol>
  );
}

export function MotorFormularioConsentimiento({ codigo }: MotorFormularioConsentimientoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id } = useParams() as { id: string };
  const [searchParams] = useSearchParams();
  const encounterParam = searchParams.get('encounter');
  const especialidad = useEspecialidadDelPracticante();
  const meta = metaConsentimiento(codigo);

  const subject = useMemo<Reference<Patient>>(() => ({ reference: `Patient/${id}` }), [id]);
  const encounter = useMemo<Reference<Encounter> | undefined>(() => (encounterParam ? { reference: encounterParam } : undefined), [encounterParam]);

  const [estado, setEstado] = useState<Estado>({ fase: 'cargando' });
  // Solo el 024 es un formulario del Anexo 1 MSP (SNS-MSP/HCU-form.024/2021): su PDF firmado lleva el bloque A y la cita
  // legal. Los otros 6 consentimientos del catálogo no son formularios del MSP y siguen con el PDF de Questionnaire.
  const esFormularioMsp = codigo === '024';
  const [camposEncabezado, setCamposEncabezado] = useState(() => valoresInicialesEncabezadoMsp());
  // Descartar (pedido explícito de Sue 2026-09-27): SOLO existe en el paso
  // 'formulario', antes de guardar — ver `handleDescartar` más abajo.
  const [descartado, setDescartado] = useState(false);
  // Bloqueo de salida sin firmar/descartar (pedido explícito de Sue,
  // 2026-09-27): pendiente desde que se crea el QuestionnaireResponse
  // (deja 'formulario') hasta 'listo' — en 'formulario' todavía no se
  // guardó nada, ahí sí se puede navegar libremente.
  const reportarPendienteFirma = useOutletContext<PatientChartOutletContext | undefined>()?.reportarPendienteFirma;
  const pendienteFirma = estado.fase !== 'cargando' && estado.fase !== 'no-encontrado' && estado.fase !== 'error' && estado.fase !== 'formulario' && estado.fase !== 'listo' && !descartado;
  useEffect(() => {
    reportarPendienteFirma?.(pendienteFirma);
  }, [reportarPendienteFirma, pendienteFirma]);
  useEffect(() => () => reportarPendienteFirma?.(false), [reportarPendienteFirma]);
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, camposEncabezado, codigo, t(`hub.formulario.${codigo}`), { conIdentificacionPaciente: true });

  useEffect(() => {
    let cancelado = false;
    medplum
      .searchOne('Questionnaire', { identifier: `http://logiacare.ec/fhir/formularios-msp|${codigo}` })
      .then((q) => {
        if (cancelado) return;
        setEstado(q ? { fase: 'formulario', questionnaire: q } : { fase: 'no-encontrado' });
      })
      .catch((err: unknown) => {
        if (cancelado) return;
        setEstado({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelado = true;
    };
  }, [medplum, codigo]);

  const handleSubmitQuestionnaire = useCallback(
    async (response: QuestionnaireResponse) => {
      if (estado.fase !== 'formulario') return;
      // Solo el 024 trae los ítems `decision`/`representante-*` (los otros 6 consentimientos siguen con `declaracion-general`): la
      // decisión es obligatoria y, si firma un representante legal, nombre + cédula + parentesco también (instructivo del MSP).
      let aGuardar = response;
      if (esFormularioMsp) {
        const errores = errores024(response);
        if (errores.length > 0) throw new Error(errores.map((e) => t(`consentimiento.error.${e}`)).join(' '));
        aGuardar = completarFechaHoraDecision(response, new Date());
      }
      const guardado = await medplum.createResource<QuestionnaireResponse>(aGuardar);
      setEstado({
        fase: 'firma-paciente',
        questionnaire: estado.questionnaire,
        response: guardado,
        aceptado: decisionDe(guardado) === 'acepta',
        porRepresentante: firmaPorRepresentante(guardado),
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [medplum, estado, esFormularioMsp, t]
  );

  async function subirFirma(dataUrl: string, contentType: string, filename: string, titulo: string): Promise<DocumentReference> {
    return subirFirmaComoDocumentReference(medplum, subject, dataUrl, contentType, filename, titulo);
  }

  const handleFirmaPaciente = useCallback(
    async (firma: FirmaPacienteCapturada) => {
      if (estado.fase !== 'firma-paciente') return;
      const { questionnaire, response } = estado;
      setEstado({ fase: 'procesando', mensaje: t('consentimiento.subiendoFirmaPaciente') });
      try {
        const docRef = await subirFirma(firma.dataUrl, firma.contentType, `firma-paciente-${firma.metodo}`, `Firma del paciente (${firma.metodo})`);
        setEstado({ fase: 'firma-profesional', questionnaire, response, firmante: { rol: 'paciente', docRefId: docRef.id!, metodo: firma.metodo } });
      } catch (err) {
        setEstado({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [medplum, subject, estado, t]
  );

  const handleNegadoSinFirma = useCallback(() => {
    if (estado.fase !== 'firma-paciente') return;
    setEstado({ fase: 'firma-testigo', questionnaire: estado.questionnaire, response: estado.response });
  }, [estado]);

  const handleFirmaTestigo = useCallback(
    async (firma: FirmaTestigoCapturada) => {
      if (estado.fase !== 'firma-testigo') return;
      const { questionnaire, response } = estado;
      setEstado({ fase: 'procesando', mensaje: t('consentimiento.subiendoFirmaTestigo') });
      try {
        const docRef = await subirFirma(firma.dataUrl, firma.contentType, 'firma-testigo', `Firma del testigo (${firma.nombre})`);
        setEstado({
          fase: 'firma-profesional',
          questionnaire,
          response,
          firmante: { rol: 'testigo', docRefId: docRef.id!, testigo: { nombre: firma.nombre, cedula: firma.cedula } },
        });
      } catch (err) {
        setEstado({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [medplum, subject, estado, t]
  );

  // Descartar (pedido explícito de Sue 2026-09-27): SOLO existe en el paso
  // 'formulario', antes de crear el QuestionnaireResponse (ver
  // `handleSubmitQuestionnaire`) — no hay nada que persistir todavía. Una vez
  // enviado ese paso, la única salida es completar la firma.
  const handleDescartar = useCallback(() => {
    setDescartado(true);
  }, []);

  const handleFirmadoProfesional = useCallback(
    async (docRefProfesional: DocumentReference) => {
      if (estado.fase !== 'firma-profesional') return;
      const { response, firmante } = estado;
      if (!meta) return;
      await construirConsentimientoInformado({
        medplum,
        response,
        subject,
        subtipo: meta.subtipo,
        policyRuleText: meta.policyRuleText,
        documentReferenceFirmaProfesional: { reference: `DocumentReference/${docRefProfesional.id}` },
        ...(firmante.rol === 'paciente'
          ? { documentReferenceFirmaPaciente: { reference: `DocumentReference/${firmante.docRefId}` } }
          : { testigo: firmante.testigo, documentReferenceFirmaTestigo: { reference: `DocumentReference/${firmante.docRefId}` } }),
      });
      setEstado({ fase: 'listo' });
    },
    [medplum, subject, estado, meta]
  );

  if (!meta) {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.noEncontradoTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.noEncontradoDetalle', { codigo })}</AlertDescription>
      </Alert>
    );
  }

  if (especialidad.cargando) {
    return <p className="text-sm text-hce-muted">{t('formulario.cargando')}</p>;
  }
  // Fail-closed cuando la especialidad no resolvió — ver el comentario largo
  // en FormularioDiscretoPage.tsx (mismo bug real, mismo fix, en los 5
  // gates del catálogo).
  if (!especialidad.codigo || !codigosFormularioDeEspecialidad(especialidad.codigo).includes(codigo)) {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.fueraDeEspecialidadTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.fueraDeEspecialidadDetalle')}</AlertDescription>
      </Alert>
    );
  }

  if (estado.fase === 'cargando') {
    return <p className="text-sm text-hce-muted">{t('formulario.cargando')}</p>;
  }
  if (estado.fase === 'no-encontrado') {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.noEncontradoTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.noEncontradoDetalle', { codigo })}</AlertDescription>
      </Alert>
    );
  }
  if (estado.fase === 'error') {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.errorTitulo')}</AlertTitle>
        <AlertDescription>{estado.mensaje}</AlertDescription>
      </Alert>
    );
  }

  if (descartado) {
    return (
      <Alert>
        <AlertTitle>{t('formulario.descartadoTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.descartadoDetalle')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-medium text-hce-ink">{t(`hub.formulario.${codigo}`)}</h3>
        {estado.fase === 'formulario' && (
          <Button type="button" variant="outline" size="sm" onClick={handleDescartar}>
            {t('formulario.descartar')}
          </Button>
        )}
      </div>
      <Indicador fase={estado.fase} />

      {esFormularioMsp && (estado.fase === 'formulario' || estado.fase === 'firma-profesional') && (
        <EncabezadoMspCampos subject={subject} value={camposEncabezado} onChange={setCamposEncabezado} />
      )}

      {estado.fase === 'formulario' && (
        <FormularioQuestionnaire
          questionnaire={estado.questionnaire}
          subject={subject}
          encounter={encounter}
          textoBoton={t('consentimiento.continuar')}
          textoBotonEnviando={t('formulario.guardando')}
          onSubmit={handleSubmitQuestionnaire}
        />
      )}

      {estado.fase === 'firma-paciente' && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-hce-muted">
            {t(
              estado.porRepresentante
                ? 'consentimiento.instruccionFirmaRepresentante'
                : estado.aceptado
                  ? 'consentimiento.instruccionFirmaPaciente'
                  : 'consentimiento.instruccionFirmaNegativa',
            )}
          </p>
          <CapturaFirmaPaciente onCapturada={(firma) => void handleFirmaPaciente(firma)} onNegadoSinFirma={estado.aceptado ? undefined : handleNegadoSinFirma} />
        </div>
      )}

      {estado.fase === 'firma-testigo' && (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-hce-muted">{t('consentimiento.instruccionFirmaTestigo')}</p>
          <CapturaFirmaTestigo onCapturada={(firma) => void handleFirmaTestigo(firma)} />
        </div>
      )}

      {estado.fase === 'firma-profesional' && (
        <div className="flex max-w-md flex-col gap-2">
          <p className="text-sm text-hce-muted">{t('consentimiento.instruccionFirmaProfesional')}</p>
          <PanelFirmaImpresion
            documentoId={`QuestionnaireResponse/${estado.response.id}`}
            tipoDocumento={codigo}
            titulo={estado.questionnaire.title ?? t(`hub.formulario.${codigo}`)}
            subject={subject}
            especificacionBuilder={async () => {
              if (esFormularioMsp) {
                return {
                  modo: 'msp',
                  pares: paresConsentimiento024({
                    questionnaire: estado.questionnaire,
                    response: estado.response,
                    paciente: { nombre: datosEncabezadoMsp.pacienteNombre, cedula: datosEncabezadoMsp.pacienteIdentificacion },
                    firmante: estado.firmante,
                  }),
                  // El servicio real lo captura el propio formulario (bloque B del papel); si no dice nada, el encabezado no imprime servicio.
                  datosEncabezadoMsp: { ...datosEncabezadoMsp, servicio: servicioAtencion024(estado.response), sinServicio: true },
                };
              }
              const paciente = await medplum.readReference(subject);
              return {
                modo: 'questionnaire',
                questionnaire: estado.questionnaire,
                response: estado.response,
                paciente: {
                  nombre: paciente.name?.[0] ? `${paciente.name[0].given?.join(' ') ?? ''} ${paciente.name[0].family ?? ''}`.trim() : subject.reference ?? '',
                  identificador: paciente.identifier?.[0]?.value,
                },
              };
            }}
            onFirmado={handleFirmadoProfesional}
          />
        </div>
      )}

      {estado.fase === 'procesando' && <p className="text-sm text-hce-muted">{estado.mensaje}</p>}

      {estado.fase === 'listo' && (
        <Alert>
          <AlertTitle>{t('consentimiento.listoTitulo')}</AlertTitle>
          <AlertDescription>{t('consentimiento.listoDetalle')}</AlertDescription>
        </Alert>
      )}
    </div>
  );
}
